import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import { useEffect, useState } from 'react';

import { useMutation } from '@tanstack/react-query';

import { validateAuthToken } from '../../services/Authentication/index';
import { deleteChatsDatabase } from '../../services/indexeddb/index';
import { RootState } from '../../store';
import { resetProfile } from '../../store/profileSlice';

type AuthOutcome =
  | { type: 'ok'; id: number }
  | { type: 'pmais' }
  | { type: 'error' };

type UseTokenOptions = {
  enabled?: boolean;
};

const RETRY_TOKEN_KEY = 'auth_retry_token';

export const useToken = ({ enabled = true }: UseTokenOptions = {}) => {
  const [isTokenValid, setIsTokenValid] = useState<boolean | null>(null);
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const profile = useSelector((state: RootState) => state.profile.profileData);

  const cleanup = () => {
    localStorage.removeItem('token');
    dispatch(resetProfile());
  };

  const goPmais = () => {
    cleanup();
    window.location.href = `${import.meta.env.VITE_PMAIS_URL}`;
  };

  const goError = () => {
    const token = localStorage.getItem('token');
    if (token) sessionStorage.setItem(RETRY_TOKEN_KEY, token);
    cleanup();
    navigate('/error', { replace: true });
  };

  const authMutation = useMutation({
    mutationKey: ['auth-token'],
    retry: false,
    mutationFn: async (token: string): Promise<AuthOutcome> => {
      return validateAuthToken(token, {
        maxAttempts: 5,
        delayMs: 1000,
      });
    },
  });

  useEffect(() => {
    if (!enabled) return;

    if (location.pathname === '/error') return;

    const validateToken = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const tokenFromUrl = urlParams.get('token');
      const tokenFromStorage = localStorage.getItem('token');

      if (tokenFromUrl) {
        dispatch(resetProfile());
      }

      const token = tokenFromUrl || tokenFromStorage;

      if (!token) {
        window.location.href = `${import.meta.env.VITE_PMAIS_URL}`;
        return;
      }

      const normalizedToken = token.trim();

      const lockKey = `auth_validation_lock:${normalizedToken}`;

      if (sessionStorage.getItem(lockKey) === '1') {
        localStorage.setItem('token', normalizedToken);
        setIsTokenValid(true);
        return;
      }

      sessionStorage.setItem(lockKey, '1');

      localStorage.setItem('token', normalizedToken);

      const outcome = await authMutation.mutateAsync(normalizedToken);

      if (outcome.type === 'ok') {
        localStorage.setItem('token', normalizedToken);
        setIsTokenValid(true);
        if (profile?.id !== outcome.id) {
          await deleteChatsDatabase();
        }
        return;
      }

      if (outcome.type === 'pmais') {
        sessionStorage.removeItem(lockKey);
        goPmais();
        return;
      }

      goError();
    };

    validateToken();
  }, [enabled, location.pathname, dispatch]);

  return { isTokenValid };
};
